import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CartContext = createContext();
export const useCart = () => useContext(CartContext);

/** Guest cart ID — persisted in localStorage as a fallback when cookies are blocked (e.g. mobile Safari ITP). */
const GUEST_KEY = 'ss_guest_cart_id';
const getGuestId = () => localStorage.getItem(GUEST_KEY);
const setGuestId = (id) => { if (id) localStorage.setItem(GUEST_KEY, id); };

/** Attach the guest-cart header so the backend can identify the cart even without cookies. */
const cartHeaders = () => {
  const gid = getGuestId();
  return gid ? { 'X-Guest-Cart-Id': gid } : {};
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart`, { withCredentials: true, headers: cartHeaders() });
      setCart(res.data);
      // Capture guest_cart_id from response header if provided
      const gid = res.headers['x-guest-cart-id'];
      if (gid) setGuestId(gid);
    } catch {
      setCart({ items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCart(); }, [fetchCart, user]);

  const addToCart = async (productId, quantity = 1, size = null, color = null) => {
    try {
      const res = await axios.post(`${API}/cart/add`, {
        product_id: productId,
        quantity,
        size,
        color
      }, { withCredentials: true, headers: cartHeaders() });
      // Capture guest_cart_id from response header
      const gid = res.headers['x-guest-cart-id'];
      if (gid) setGuestId(gid);
      await fetchCart();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed to add to cart' };
    }
  };

  const updateCartItem = async (productId, quantity, size = null, color = null) => {
    try {
      await axios.put(`${API}/cart/update`, {
        product_id: productId,
        quantity,
        size,
        color
      }, { withCredentials: true, headers: cartHeaders() });
      await fetchCart();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed to update item' };
    }
  };

  const removeFromCart = async (productId, size = null, color = null) => {
    try {
      const params = new URLSearchParams();
      if (size) params.append('size', size);
      if (color) params.append('color', color);
      await axios.delete(`${API}/cart/remove/${productId}?${params}`, { withCredentials: true, headers: cartHeaders() });
      await fetchCart();
      return { success: true };
    } catch (e) {
      return { success: false, error: e.response?.data?.detail || 'Failed to remove item' };
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/clear`, { withCredentials: true, headers: cartHeaders() });
      setCart({ items: [], total: 0 });
    } catch {
      // silent
    }
  };

  const mergeGuestCart = async () => {
    try {
      await axios.post(`${API}/cart/merge`, {}, { withCredentials: true, headers: cartHeaders() });
      await fetchCart();
    } catch {
      // silent
    }
  };

  const itemCount = cart.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;

  const value = {
    cart, loading, itemCount,
    addToCart, updateCartItem, removeFromCart, clearCart, fetchCart, mergeGuestCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
